import { ROLE_LABEL } from "@love-cpmd/core";
import { Avatar, Badge, cn } from "@love-cpmd/ui";
import { LogOut } from "lucide-react";
import { Link, NavLink } from "react-router-dom";
import { useAccess, useAuth } from "../../lib/auth";
import { Logo, LogoMark } from "../common/layout-bits";
import { ADMIN_NAV, MAIN_NAV, type NavItem } from "./nav";

function useVisibleNav() {
  const { can } = useAccess();
  return {
    main: MAIN_NAV.filter((i) => !i.cap || can(i.cap)),
    admin: can("admin.access") ? ADMIN_NAV : undefined,
  };
}

const itemBase =
  "group relative flex items-center gap-3 rounded-control px-3 py-2.5 text-label-lg transition-colors duration-150 [&_svg]:size-5 [&_svg]:shrink-0";
const itemIdle = "text-on-surface-variant hover:bg-surface-container-low hover:text-on-surface";
const itemActive =
  "bg-primary-fixed/80 text-primary font-bold before:absolute before:top-2 before:bottom-2 before:left-0 before:w-1 before:rounded-r-full before:bg-primary-container";

function SideLink({ item }: { item: NavItem }) {
  return (
    <NavLink to={item.to} className={({ isActive }) => cn(itemBase, isActive ? itemActive : itemIdle)}>
      <item.icon aria-hidden />
      <span className="truncate">{item.label}</span>
    </NavLink>
  );
}

/** Desktop ≥1024px: fixed 240px sidebar. */
export function Sidebar() {
  const { main, admin } = useVisibleNav();
  const { user } = useAccess();
  const { logout } = useAuth();
  return (
    <aside className="fixed inset-y-0 left-0 z-40 hidden w-60 flex-col border-r border-outline-variant/40 bg-surface-container-lowest lg:flex">
      <div className="flex h-16 items-center border-b border-outline-variant/30 px-4">
        <Link to="/dashboard" aria-label="LOVE CPMD home">
          <Logo />
        </Link>
      </div>
      <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-3 pt-5" aria-label="Main menu">
        <p className="px-3 pb-1.5 text-label-sm font-bold tracking-wider text-on-surface-variant/80 uppercase">Main menu</p>
        {main.map((item) => (
          <SideLink key={item.to} item={item} />
        ))}
      </nav>
      <div className="flex flex-col gap-2 border-t border-outline-variant/30 p-3">
        {admin && <SideLink item={admin} />}
        {user && (
          <div className="flex items-center gap-2 rounded-2xl bg-surface-container-low p-2">
            <Link to="/profile" className="flex min-w-0 flex-1 items-center gap-2.5 rounded-xl p-1 hover:bg-surface-container" title="Profile">
              <Avatar name={user.name} seed={user.memberId} src={user.avatarUrl} size="sm" />
              <span className="flex min-w-0 flex-col">
                <span className="flex items-center gap-1.5">
                  <span className="truncate text-label-md text-on-surface">{user.nickname}</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="truncate text-body-sm text-on-surface-variant">{user.memberId}</span>
                  <Badge tone={user.role === "system_manager" ? "brand" : user.role === "guest" ? "neutral" : "secondary"} size="xs">
                    {user.role === "system_manager" ? "SM" : ROLE_LABEL[user.role]}
                  </Badge>
                </span>
              </span>
            </Link>
            <button
              type="button"
              onClick={logout}
              title="Log out"
              aria-label="Log out"
              className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg text-on-surface-variant transition-colors hover:bg-error-container/50 hover:text-error"
            >
              <LogOut className="size-[18px]" />
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}

/** Tablet 768–1023px: 72px icon rail, labels appear on hover/focus. */
export function Rail() {
  const { main, admin } = useVisibleNav();
  const { user } = useAccess();
  const { logout } = useAuth();
  const railItem = (item: NavItem) => (
    <NavLink
      key={item.to}
      to={item.to}
      aria-label={item.label}
      className={({ isActive }) =>
        cn(
          "group relative flex size-12 items-center justify-center rounded-2xl transition-colors [&_svg]:size-[22px]",
          isActive ? "bg-primary-fixed text-primary" : "text-on-surface-variant hover:bg-surface-container-low hover:text-on-surface",
        )
      }
    >
      <item.icon aria-hidden />
      <span className="pointer-events-none absolute left-[calc(100%+10px)] z-50 rounded-lg bg-inverse-surface px-2.5 py-1.5 text-label-md whitespace-nowrap text-inverse-on-surface opacity-0 shadow-overlay transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
        {item.label}
      </span>
    </NavLink>
  );
  return (
    <aside className="fixed inset-y-0 left-0 z-40 hidden w-[72px] flex-col items-center border-r border-outline-variant/40 bg-surface-container-lowest py-3 md:flex lg:hidden">
      <Link to="/dashboard" aria-label="LOVE CPMD home" className="mb-4">
        <LogoMark className="size-10" decorative />
      </Link>
      <nav className="flex flex-1 flex-col items-center gap-1.5" aria-label="Main menu">
        {main.map(railItem)}
      </nav>
      <div className="flex flex-col items-center gap-1.5 border-t border-outline-variant/30 pt-3">
        {admin && railItem(admin)}
        {user && (
          <Link to="/profile" aria-label="Profile" className="rounded-full p-1 hover:bg-surface-container">
            <Avatar name={user.name} seed={user.memberId} src={user.avatarUrl} size="sm" />
          </Link>
        )}
        <button
          type="button"
          onClick={logout}
          aria-label="Log out"
          title="Log out"
          className="inline-flex size-10 items-center justify-center rounded-xl text-on-surface-variant hover:bg-error-container/50 hover:text-error"
        >
          <LogOut className="size-5" />
        </button>
      </div>
    </aside>
  );
}
